import { useCallback, useEffect, useRef, useState } from "react";
import { useStore } from "react-redux";
import { useAppDispatch, useAppSelector, type RootState } from "@app/store";
import { selectAccessToken } from "@app/store/auth.slice";
import type {
    GroupItem,
    GroupsSummary,
    GroupsPageInfo,
    GroupDetailItem,
    GroupMemberUser,
    GroupMembersPageInfo,
    GroupBatchUser,
    GroupBatchPageInfo,
} from "@app/types/groups";
import { GroupApproverMode } from "@app/types/groups";
import type { UserSortField } from "@app/constants/user-sort-fields";
import { ROLE_KEY } from "@app/types/permissions";
import { GroupConcurrencyError, makeGql } from "@app/utils/gql";

export { GroupConcurrencyError };

const GRAPHQL_ENDPOINT = import.meta.env.VITE_GRAPHQL_ENDPOINT ?? "";
const APPSYNC_API_KEY = import.meta.env.VITE_GRAPHQL_API_KEY ?? "";

function useApiHeaders() {
    return useCallback((): Record<string, string> => {
        const h: Record<string, string> = {};
        if (APPSYNC_API_KEY) h["x-api-key"] = APPSYNC_API_KEY;
        return h;
    }, []);
}

// ─── useGroups (dropdown list) ────────────────────────────────────────────────
// Users page group picker — mirrors `groups`, gated by the Users permission (not Groups).

const GROUPS_QUERY = `
  query UserGroups {
    userGroups {
      groups {
        groupId
        groupName
      }
    }
  }
`;

const GROUPS_DROPDOWN_QUERY = `
  query GroupsDropdown($limit: Int, $cursor: String) {
    groups(limit: $limit, cursor: $cursor) {
      pageInfo {
        cursor
        hasNextPage
      }
      groups {
        groupId
        groupName
      }
    }
  }
`;

export function useGroupsForDropdown() {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const accessToken = useAppSelector(selectAccessToken);
    void accessToken;

    const [groups, setGroups] = useState<Pick<GroupItem, "groupId" | "groupName">[]>([]);
    const [cursor, setCursor] = useState<string | null>(null);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const initialLoadedRef = useRef(false);
    const headers = useApiHeaders();
    const gql = useCallback(makeGql(dispatch, store, headers, GRAPHQL_ENDPOINT), [dispatch, store, headers]);

    useEffect(() => {
        if (initialLoadedRef.current) return;
        initialLoadedRef.current = true;
        setIsLoading(true);

        gql<{ groups: { pageInfo: { cursor: string | null; hasNextPage: boolean }; groups: Pick<GroupItem, "groupId" | "groupName">[] } }>(
            GROUPS_DROPDOWN_QUERY,
            { limit: 50 },
        )
            .then((data) => {
                setGroups(data.groups.groups);
                setCursor(data.groups.pageInfo.cursor ?? null);
                setHasNextPage(data.groups.pageInfo.hasNextPage);
            })
            .catch((err) => {
                console.error("[use-groups] Failed to load initial groups page:", err);
            })
            .finally(() => setIsLoading(false));
    }, [gql]);

    const fetchNextPage = useCallback(() => {
        if (!hasNextPage || isLoading) return;
        setIsLoading(true);

        gql<{ groups: { pageInfo: { cursor: string | null; hasNextPage: boolean }; groups: Pick<GroupItem, "groupId" | "groupName">[] } }>(
            GROUPS_DROPDOWN_QUERY,
            { limit: 50, cursor },
        )
            .then((data) => {
                setGroups((prev) => [...prev, ...data.groups.groups]);
                setCursor(data.groups.pageInfo.cursor ?? null);
                setHasNextPage(data.groups.pageInfo.hasNextPage);
            })
            .catch((err) => {
                console.error("[use-groups] Failed to load next groups page:", err);
            })
            .finally(() => setIsLoading(false));
    }, [gql, cursor, hasNextPage, isLoading]);

    return { groups, isLoading, hasNextPage, fetchNextPage };
}

export function useGroups() {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const accessToken = useAppSelector(selectAccessToken);
    void accessToken;

    const [groups, setGroups] = useState<Pick<GroupItem, "groupId" | "groupName">[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const inflightRef = useRef<Set<string>>(new Set());
    const headers = useApiHeaders();

    const gql = useCallback(makeGql(dispatch, store, headers, GRAPHQL_ENDPOINT), [dispatch, store, headers]);

    useEffect(() => {
        const key = "groups";
        if (inflightRef.current.has(key)) return;
        inflightRef.current.add(key);

        setIsLoading(true);
        setError(null);

        gql<{ userGroups: { groups: Pick<GroupItem, "groupId" | "groupName">[] } }>(GROUPS_QUERY)
            .then((data) => {
                setGroups(data.userGroups.groups);
            })
            .catch((err) => {
                setError(err instanceof Error ? err.message : "Failed to load groups");
            })
            .finally(() => {
                inflightRef.current.delete(key);
                setIsLoading(false);
            });
    }, [gql]);

    return { groups, isLoading, error };
}

// ─── useGroupsList ────────────────────────────────────────────────────────────

export type GroupSortBy = "name" | "status" | "createdAt" | "updatedAt";

export interface GroupsQueryFilters {
    name?: string;
    status?: string;
    sortBy?: GroupSortBy;
    sortOrder?: "asc" | "desc";
}

const GROUPS_LIST_QUERY = `
  query GroupsList($limit: Int, $cursor: String, $name: String, $status: String, $sortBy: String, $sortOrder: String) {
    groups(limit: $limit, cursor: $cursor, name: $name, status: $status, sortBy: $sortBy, sortOrder: $sortOrder) {
      summary {
        totalGroups
        assignedUsers
        unassignedUsers
      }
      pageInfo {
        totalCount
        cursor
        limit
        hasNextPage
      }
      groups {
        groupId
        groupName
        isActive
        memberCount
        createdAt
        updatedAt
        updatedBy
        policyExceptions {
          id
        }
        policyIds
      }
    }
  }
`;

const GROUPS_PAGE_LIMIT = 10;

export function useGroupsList(filters: GroupsQueryFilters = {}) {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const accessToken = useAppSelector(selectAccessToken);
    void accessToken;

    const [groupItems, setGroupItems] = useState<GroupItem[]>([]);
    const [summary, setSummary] = useState<GroupsSummary | null>(null);
    const [pageInfo, setPageInfo] = useState<GroupsPageInfo | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const cursorHistoryRef = useRef<(string | null)[]>([null]);
    const inflightRef = useRef<Set<string>>(new Set());
    const currentRequestKeyRef = useRef<string | null>(null);
    const filtersRef = useRef(filters);
    filtersRef.current = filters;

    const filterKey = `${filters.name ?? ""}|${filters.status ?? ""}|${filters.sortBy ?? ""}|${filters.sortOrder ?? ""}`;
    const prevFilterKeyRef = useRef(filterKey);

    const headers = useApiHeaders();
    const gql = useCallback(makeGql(dispatch, store, headers, GRAPHQL_ENDPOINT), [dispatch, store, headers]);

    const loadGroups = useCallback(
        async (page: number) => {
            const cursor = cursorHistoryRef.current[page] ?? undefined;
            const key = `groups-page-${page}-cursor-${cursor ?? "null"}-${JSON.stringify(filtersRef.current)}`;

            if (inflightRef.current.has(key)) return;
            inflightRef.current.add(key);
            currentRequestKeyRef.current = key;

            setIsLoading(true);
            setError(null);

            try {
                const f = filtersRef.current;
                const data = await gql<{ groups: { summary: GroupsSummary; pageInfo: GroupsPageInfo; groups: GroupItem[] } }>(GROUPS_LIST_QUERY, {
                    limit: GROUPS_PAGE_LIMIT,
                    cursor,
                    ...(f.name ? { name: f.name } : {}),
                    ...(f.status ? { status: f.status } : {}),
                    ...(f.sortBy ? { sortBy: f.sortBy } : {}),
                    ...(f.sortOrder ? { sortOrder: f.sortOrder } : {}),
                });

                if (currentRequestKeyRef.current !== key) return;

                setGroupItems(data.groups.groups);
                setSummary(data.groups.summary);
                setPageInfo(data.groups.pageInfo);

                if (data.groups.pageInfo.cursor && !cursorHistoryRef.current[page + 1]) {
                    cursorHistoryRef.current[page + 1] = data.groups.pageInfo.cursor;
                }
            } catch (err) {
                if (currentRequestKeyRef.current !== key) return;
                setError(err instanceof Error ? err.message : "Failed to load groups");
            } finally {
                inflightRef.current.delete(key);
                if (currentRequestKeyRef.current === key) setIsLoading(false);
            }
        },
        [gql],
    );

    const [refreshToken, setRefreshToken] = useState(0);

    useEffect(() => {
        if (prevFilterKeyRef.current === filterKey) return;
        prevFilterKeyRef.current = filterKey;
        cursorHistoryRef.current = [null];
        inflightRef.current.clear();
        setCurrentPage(0);
        setRefreshToken((t) => t + 1);
    }, [filterKey]);

    useEffect(() => {
        void loadGroups(currentPage);
    }, [loadGroups, currentPage, refreshToken]);

    const setPage = useCallback((page: number) => {
        setCurrentPage(page);
    }, []);

    const refresh = useCallback(() => {
        cursorHistoryRef.current = [null];
        setCurrentPage(0);
        setRefreshToken((t) => t + 1);
    }, []);

    return { groupItems, summary, pageInfo, currentPage, isLoading, error, setPage, refresh };
}

// ─── useGroupDetail ───────────────────────────────────────────────────────────

const GET_GROUP_BY_ID_QUERY = `
  query GetGroupById($groupId: ID!) {
    getGroupById(groupId: $groupId) {
      groupId
      groupName
      description
      companyId
      isActive
      isDefault
      createdAt
      createdBy
      updatedAt
      updatedBy
      members
      approverConfig {
        approverMode
        approvers
        backupApprovers
      }
      policyExceptions {
        id
      }
      notifyEmail
      customFields {
        id
        label
      }
      groupEpoch
      policyIds
      accountants
    }
  }
`;

export function useGroupDetail(groupId: string | null) {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const accessToken = useAppSelector(selectAccessToken);
    void accessToken;

    const [group, setGroup] = useState<GroupDetailItem | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [reloadKey, setReloadKey] = useState(0);

    const inflightRef = useRef<Set<string>>(new Set());
    const currentGroupIdRef = useRef<string | null>(null);
    const headers = useApiHeaders();

    const gql = useCallback(makeGql(dispatch, store, headers, GRAPHQL_ENDPOINT), [dispatch, store, headers]);

    const reload = useCallback(() => setReloadKey((k) => k + 1), []);

    useEffect(() => {
        if (!groupId) {
            currentGroupIdRef.current = null;
            setGroup(null);
            setIsLoading(false);
            setError(null);
            return;
        }

        currentGroupIdRef.current = groupId;

        // Include reloadKey in the key so each reload bypasses the inflight deduplication
        const key = `group-${groupId}-${reloadKey}`;
        if (inflightRef.current.has(key)) return;
        inflightRef.current.add(key);

        setIsLoading(true);
        setError(null);
        setGroup(null);

        gql<{ getGroupById: GroupDetailItem }>(GET_GROUP_BY_ID_QUERY, { groupId })
            .then((data) => {
                if (currentGroupIdRef.current === groupId) setGroup(data.getGroupById);
            })
            .catch((err) => {
                if (currentGroupIdRef.current === groupId) setError(err instanceof Error ? err.message : "Failed to load group");
            })
            .finally(() => {
                inflightRef.current.delete(key);
                if (currentGroupIdRef.current === groupId) setIsLoading(false);
            });
    }, [groupId, reloadKey, gql]);

    return { group, isLoading, error, reload };
}

// ─── useUpdateGroup ───────────────────────────────────────────────────────────

type UpdateGroupResult = Pick<
    GroupDetailItem,
    | "groupId"
    | "groupName"
    | "description"
    | "companyId"
    | "isActive"
    | "isDefault"
    | "createdAt"
    | "createdBy"
    | "updatedAt"
    | "updatedBy"
    | "groupEpoch"
    | "policyIds"
>;

const UPDATE_GROUP_MUTATION = `
  mutation UpdateGroup(
    $groupId: ID!
    $groupName: String!
    $groupEpoch: String!
    $policyIds: [ID!]!
    $accountants: [ID!]!
    $approverConfig: GroupApproverConfigInput!
  ) {
    updateGroup(
      groupId: $groupId
      groupName: $groupName
      groupEpoch: $groupEpoch
      policyIds: $policyIds
      accountants: $accountants
      approverConfig: $approverConfig
    ) {
      groupId
      groupName
      groupEpoch
      policyIds
      accountants
      approverConfig {
        approverMode
        approvers
        backupApprovers
      }
    }
  }
`;

export function useUpdateGroup() {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const accessToken = useAppSelector(selectAccessToken);
    void accessToken;

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const headers = useApiHeaders();
    const gql = useCallback(makeGql(dispatch, store, headers, GRAPHQL_ENDPOINT), [dispatch, store, headers]);

    const updateGroup = useCallback(
        async (
            groupId: string,
            groupName: string,
            groupEpoch: string,
            policyIds: string[],
            approverConfig: { approverMode: GroupApproverMode; approvers: string[] } = { approverMode: GroupApproverMode.Manager, approvers: [] },
            accountants: string[] = [],
        ): Promise<void> => {
            if (isMountedRef.current) {
                setIsLoading(true);
                setError(null);
            }
            try {
                await gql<{ updateGroup: UpdateGroupResult }>(UPDATE_GROUP_MUTATION, {
                    groupId,
                    groupName,
                    groupEpoch,
                    policyIds,
                    approverConfig,
                    accountants,
                });
            } catch (err) {
                if (isMountedRef.current) {
                    const message = err instanceof Error ? err.message : "Failed to update group";
                    setError(message);
                }
                throw err;
            } finally {
                if (isMountedRef.current) setIsLoading(false);
            }
        },
        [gql],
    );

    const clearError = useCallback(() => setError(null), []);

    return { updateGroup, isLoading, error, clearError };
}

// ─── useCreateGroup ───────────────────────────────────────────────────────────
// isDefault is always sent as false by the BFF — making a group the company default
// isn't exposed here. Members must be added via a follow-up assignGroupMembers call,
// since the groups service ignores members on create.

type CreateGroupResult = Pick<GroupDetailItem, "groupId" | "groupName" | "groupEpoch" | "policyIds" | "accountants" | "approverConfig">;

const CREATE_GROUP_MUTATION = `
  mutation CreateGroup(
    $groupName: String!
    $policyIds: [ID!]
    $accountants: [ID!]
    $approverConfig: GroupApproverConfigInput
  ) {
    createGroup(
      groupName: $groupName
      policyIds: $policyIds
      accountants: $accountants
      approverConfig: $approverConfig
    ) {
      groupId
      groupName
      groupEpoch
      policyIds
      accountants
      approverConfig {
        approverMode
        approvers
        backupApprovers
      }
    }
  }
`;

export function useCreateGroup() {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const accessToken = useAppSelector(selectAccessToken);
    void accessToken;

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const headers = useApiHeaders();
    const gql = useCallback(makeGql(dispatch, store, headers, GRAPHQL_ENDPOINT), [dispatch, store, headers]);

    const createGroup = useCallback(
        async (
            groupName: string,
            policyIds: string[] = [],
            approverConfig: { approverMode: GroupApproverMode; approvers: string[] } = { approverMode: GroupApproverMode.Manager, approvers: [] },
            accountants: string[] = [],
        ): Promise<CreateGroupResult> => {
            if (isMountedRef.current) {
                setIsLoading(true);
                setError(null);
            }
            try {
                const data = await gql<{ createGroup: CreateGroupResult }>(CREATE_GROUP_MUTATION, {
                    groupName,
                    policyIds,
                    accountants,
                    approverConfig,
                });
                return data.createGroup;
            } catch (err) {
                if (isMountedRef.current) {
                    const message = err instanceof Error ? err.message : "Failed to create group";
                    setError(message);
                }
                throw err;
            } finally {
                if (isMountedRef.current) setIsLoading(false);
            }
        },
        [gql],
    );

    const clearError = useCallback(() => setError(null), []);

    return { createGroup, isLoading, error, clearError };
}

// ─── useApproverAndAccountantRoleIds ─────────────────────────────────────────
// Resolves the `roleId` for the two system roles the Groups page cares about,
// since `groupRoleUsers` takes a roleId, not a roleKey.

const GROUPS_ROLES_QUERY = `
  query GroupsRoles {
    roles(isSystemDefined: true) {
      roleId
      roleKey
    }
  }
`;

export function useApproverAndAccountantRoleIds() {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const accessToken = useAppSelector(selectAccessToken);
    void accessToken;

    const [approverRoleId, setApproverRoleId] = useState<string | null>(null);
    const [accountantRoleId, setAccountantRoleId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const inflightRef = useRef(false);
    const headers = useApiHeaders();
    const gql = useCallback(makeGql(dispatch, store, headers, GRAPHQL_ENDPOINT), [dispatch, store, headers]);

    useEffect(() => {
        if (inflightRef.current) return;
        inflightRef.current = true;
        setIsLoading(true);

        gql<{ roles: { roleId: string; roleKey: string }[] }>(GROUPS_ROLES_QUERY, {})
            .then((data) => {
                setApproverRoleId(data.roles.find((r) => r.roleKey === ROLE_KEY.Approver)?.roleId ?? null);
                setAccountantRoleId(data.roles.find((r) => r.roleKey === ROLE_KEY.GroupAccountingApprover)?.roleId ?? null);
            })
            .catch((err) => console.error("[useApproverAndAccountantRoleIds]", err))
            .finally(() => {
                inflightRef.current = false;
                setIsLoading(false);
            });
    }, [gql]);

    return { approverRoleId, accountantRoleId, isLoading };
}

// ─── useGroupRoleUsers ────────────────────────────────────────────────────────
// Server-side name search over the users assigned to a role, for the
// "add approver/accountant" picker. Replaces the removed `Role.users`.

const GROUP_ROLE_USERS_QUERY = `
  query GroupRoleUsers($roleId: ID!, $filter: UsersByBatchFilter, $limit: Int, $cursor: String) {
    groupRoleUsers(roleId: $roleId, filter: $filter, limit: $limit, cursor: $cursor) {
      items {
        userId
        emailId
        legalName { firstName lastName }
      }
      pageInfo {
        hasMore
        nextCursor
      }
    }
  }
`;

export interface ApproverUser {
    userId: string;
    emailId: string | null;
    legalName: { firstName: string | null; lastName: string | null } | null;
}

type GroupRoleUsersResult = {
    groupRoleUsers: {
        items: { userId: string; emailId: string | null; legalName: ApproverUser["legalName"] }[];
        pageInfo: { hasMore: boolean; nextCursor: string | null };
    };
};

export function useGroupRoleUsers(roleId: string | null, nameFilter: string, enabled: boolean = true, limit: number = 20) {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const accessToken = useAppSelector(selectAccessToken);
    void accessToken;

    const [users, setUsers] = useState<ApproverUser[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const requestIdRef = useRef(0);
    const nextCursorRef = useRef<string | null>(null);

    const headers = useApiHeaders();
    const gql = useCallback(makeGql(dispatch, store, headers, GRAPHQL_ENDPOINT), [dispatch, store, headers]);

    useEffect(() => {
        // Only fetch while the picker is open — don't fire on page load. An empty
        // name filter returns the default (unfiltered) list of role users.
        if (!roleId || !enabled) {
            setUsers([]);
            setHasMore(false);
            nextCursorRef.current = null;
            return;
        }
        const requestId = ++requestIdRef.current;
        setIsLoading(true);
        setError(null);

        gql<GroupRoleUsersResult>(GROUP_ROLE_USERS_QUERY, {
            roleId,
            filter: nameFilter ? { name: nameFilter } : undefined,
            limit,
        })
            .then((data) => {
                if (requestIdRef.current !== requestId) return;
                setUsers(data.groupRoleUsers.items.map((u) => ({ userId: u.userId, emailId: u.emailId, legalName: u.legalName })));
                setHasMore(data.groupRoleUsers.pageInfo.hasMore);
                nextCursorRef.current = data.groupRoleUsers.pageInfo.nextCursor;
            })
            .catch((err) => {
                if (requestIdRef.current !== requestId) return;
                setError(err instanceof Error ? err.message : "Failed to search users");
            })
            .finally(() => {
                if (requestIdRef.current === requestId) setIsLoading(false);
            });
    }, [gql, roleId, nameFilter, enabled, limit]);

    const loadMore = useCallback(() => {
        if (!roleId || !hasMore || !nextCursorRef.current || isLoading || isLoadingMore) return;
        const requestId = ++requestIdRef.current;
        const cursor = nextCursorRef.current;
        setIsLoadingMore(true);

        gql<GroupRoleUsersResult>(GROUP_ROLE_USERS_QUERY, {
            roleId,
            filter: nameFilter ? { name: nameFilter } : undefined,
            limit,
            cursor,
        })
            .then((data) => {
                if (requestIdRef.current !== requestId) return;
                setUsers((prev) => [...prev, ...data.groupRoleUsers.items.map((u) => ({ userId: u.userId, emailId: u.emailId, legalName: u.legalName }))]);
                setHasMore(data.groupRoleUsers.pageInfo.hasMore);
                nextCursorRef.current = data.groupRoleUsers.pageInfo.nextCursor;
            })
            .catch((err) => {
                if (requestIdRef.current !== requestId) return;
                setError(err instanceof Error ? err.message : "Failed to load more users");
            })
            .finally(() => {
                if (requestIdRef.current === requestId) setIsLoadingMore(false);
            });
    }, [gql, roleId, nameFilter, limit, hasMore, isLoading, isLoadingMore]);

    return { users, isLoading, isLoadingMore, hasMore, loadMore, error };
}

// ─── useGroupUsersSearch ──────────────────────────────────────────────────────
// Company-wide user search (no groupId scoping) for the "search & add members"
// picker in the Group drawer. Mirrors useGroupRoleUsers's cursor-append shape,
// but only fires once the name filter has at least 3 characters — searching the
// whole company on 1-2 chars would be too noisy/expensive.

const GROUP_USERS_QUERY = `
  query GroupUsers($limit: Int, $cursor: String, $name: String, $email: String, $isExpenseUser: Boolean) {
    groupUsers(limit: $limit, cursor: $cursor, name: $name, email: $email, isExpenseUser: $isExpenseUser) {
      users {
        userId
        legalName { firstName lastName }
        emailId
        status
      }
      pageInfo {
        cursor
        hasNextPage
      }
    }
  }
`;

export interface GroupUserSearchResult {
    userId: string;
    emailId: string | null;
    legalName: { firstName: string | null; lastName: string | null } | null;
}

type GroupUsersQueryResult = {
    groupUsers: {
        users: GroupUserSearchResult[];
        pageInfo: { cursor: string | null; hasNextPage: boolean };
    };
};

const GROUP_USERS_MIN_CHARS = 3;

export function useGroupUsersSearch(nameFilter: string, enabled: boolean = true, limit: number = 20) {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const accessToken = useAppSelector(selectAccessToken);
    void accessToken;

    const [users, setUsers] = useState<GroupUserSearchResult[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const requestIdRef = useRef(0);
    const nextCursorRef = useRef<string | null>(null);

    const headers = useApiHeaders();
    const gql = useCallback(makeGql(dispatch, store, headers, GRAPHQL_ENDPOINT), [dispatch, store, headers]);

    const trimmedFilter = nameFilter.trim();
    const belowMinChars = trimmedFilter.length < GROUP_USERS_MIN_CHARS;

    useEffect(() => {
        if (!enabled || belowMinChars) {
            requestIdRef.current++; // invalidate any in-flight request so its late response can't repopulate state
            setUsers([]);
            setHasMore(false);
            nextCursorRef.current = null;
            return;
        }
        const requestId = ++requestIdRef.current;
        setIsLoading(true);
        setError(null);

        gql<GroupUsersQueryResult>(GROUP_USERS_QUERY, {
            name: trimmedFilter,
            limit,
            isExpenseUser: true,
        })
            .then((data) => {
                if (requestIdRef.current !== requestId) return;
                setUsers(data.groupUsers.users.map((u) => ({ userId: u.userId, emailId: u.emailId, legalName: u.legalName })));
                setHasMore(data.groupUsers.pageInfo.hasNextPage);
                nextCursorRef.current = data.groupUsers.pageInfo.cursor;
            })
            .catch((err) => {
                if (requestIdRef.current !== requestId) return;
                setError(err instanceof Error ? err.message : "Failed to search users");
            })
            .finally(() => {
                if (requestIdRef.current === requestId) setIsLoading(false);
            });
    }, [gql, trimmedFilter, belowMinChars, enabled, limit]);

    const loadMore = useCallback(() => {
        if (belowMinChars || !hasMore || !nextCursorRef.current || isLoading || isLoadingMore) return;
        const requestId = ++requestIdRef.current;
        const cursor = nextCursorRef.current;
        setIsLoadingMore(true);

        gql<GroupUsersQueryResult>(GROUP_USERS_QUERY, {
            name: trimmedFilter,
            limit,
            cursor,
            isExpenseUser: true,
        })
            .then((data) => {
                if (requestIdRef.current !== requestId) return;
                setUsers((prev) => [...prev, ...data.groupUsers.users.map((u) => ({ userId: u.userId, emailId: u.emailId, legalName: u.legalName }))]);
                setHasMore(data.groupUsers.pageInfo.hasNextPage);
                nextCursorRef.current = data.groupUsers.pageInfo.cursor;
            })
            .catch((err) => {
                if (requestIdRef.current !== requestId) return;
                setError(err instanceof Error ? err.message : "Failed to load more users");
            })
            .finally(() => {
                if (requestIdRef.current === requestId) setIsLoadingMore(false);
            });
    }, [gql, trimmedFilter, limit, hasMore, isLoading, isLoadingMore, belowMinChars]);

    return { users, isLoading, isLoadingMore, hasMore, loadMore, error, belowMinChars };
}

// ─── useAssignGroupMembers ────────────────────────────────────────────────────

const ASSIGN_GROUP_MEMBERS_MUTATION = `
  mutation AssignGroupMembers($groupId: ID!, $userIds: [ID!]!) {
    assignGroupMembers(groupId: $groupId, userIds: $userIds) {
      succeededUserIds
      failed {
        userId
        reason
      }
    }
  }
`;

export interface AssignGroupMembersResult {
    succeededUserIds: string[];
    failed: { userId: string; reason: string }[];
}

export function useAssignGroupMembers() {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const accessToken = useAppSelector(selectAccessToken);
    void accessToken;

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const headers = useApiHeaders();
    const gql = useCallback(makeGql(dispatch, store, headers, GRAPHQL_ENDPOINT), [dispatch, store, headers]);

    const assignGroupMembers = useCallback(
        async (groupId: string, userIds: string[]): Promise<AssignGroupMembersResult> => {
            if (isMountedRef.current) {
                setIsLoading(true);
                setError(null);
            }
            try {
                const data = await gql<{ assignGroupMembers: AssignGroupMembersResult }>(ASSIGN_GROUP_MEMBERS_MUTATION, {
                    groupId,
                    userIds,
                });
                return data.assignGroupMembers;
            } catch (err) {
                if (isMountedRef.current) {
                    const message = err instanceof Error ? err.message : "Failed to add members";
                    setError(message);
                }
                throw err;
            } finally {
                if (isMountedRef.current) setIsLoading(false);
            }
        },
        [gql],
    );

    const clearError = useCallback(() => setError(null), []);

    return { assignGroupMembers, isLoading, error, clearError };
}

// ─── useGroupMembers ──────────────────────────────────────────────────────────

export type GroupMembersSortBy = UserSortField;

export interface GroupMembersQueryFilters {
    name?: string;
    email?: string;
    status?: string;
    sortBy?: GroupMembersSortBy;
    sortOrder?: "asc" | "desc";
}

const GROUP_MEMBERS_QUERY = `
  query GroupMembers($groupId: ID!, $limit: Int, $cursor: String, $name: String, $email: String, $status: String, $sortBy: String, $sortOrder: String) {
    groupMembers(groupId: $groupId, limit: $limit, cursor: $cursor, name: $name, email: $email, status: $status, sortBy: $sortBy, sortOrder: $sortOrder) {
      users {
        userId
        legalName { firstName lastName }
        emailId
        status
      }
      pageInfo {
        totalCount
        cursor
        limit
        hasNextPage
      }
    }
  }
`;

export function useGroupMembers(groupId: string | null, initialLimit: number, filters: GroupMembersQueryFilters = {}) {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const accessToken = useAppSelector(selectAccessToken);
    void accessToken;

    const [users, setUsers] = useState<GroupMemberUser[]>([]);
    const [pageInfo, setPageInfo] = useState<GroupMembersPageInfo | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [reloadKey, setReloadKey] = useState(0);
    const cursorHistoryRef = useRef<(string | null)[]>([null]);
    const inflightRef = useRef<Set<string>>(new Set());
    const filtersRef = useRef(filters);
    filtersRef.current = filters;

    const filterKey = `${filters.name ?? ""}|${filters.email ?? ""}|${filters.status ?? ""}|${filters.sortBy ?? ""}|${filters.sortOrder ?? ""}`;
    const prevFilterKeyRef = useRef(filterKey);

    const headers = useApiHeaders();
    const gql = useCallback(makeGql(dispatch, store, headers, GRAPHQL_ENDPOINT), [dispatch, store, headers]);

    useEffect(() => {
        if (prevFilterKeyRef.current === filterKey) return;
        prevFilterKeyRef.current = filterKey;
        cursorHistoryRef.current = [null];
        setCurrentPage(0);
    }, [filterKey]);

    useEffect(() => {
        if (!groupId) {
            setUsers([]);
            setPageInfo(null);
            setCurrentPage(0);
            cursorHistoryRef.current = [null];
            return;
        }

        const cursor = cursorHistoryRef.current[currentPage] ?? undefined;
        const f = filtersRef.current;
        const key = `group-members-${groupId}-page${currentPage}-cursor${cursor ?? "null"}-${reloadKey}-${filterKey}`;
        if (inflightRef.current.has(key)) return;
        inflightRef.current.add(key);
        setIsLoading(true);

        gql<{ groupMembers: { users: GroupMemberUser[]; pageInfo: GroupMembersPageInfo } }>(GROUP_MEMBERS_QUERY, {
            groupId,
            limit: initialLimit,
            cursor,
            ...(f.name ? { name: f.name } : {}),
            ...(f.email ? { email: f.email } : {}),
            ...(f.status ? { status: f.status } : {}),
            ...(f.sortBy ? { sortBy: f.sortBy } : {}),
            ...(f.sortOrder ? { sortOrder: f.sortOrder } : {}),
        })
            .then((data) => {
                setUsers(data.groupMembers.users);
                setPageInfo(data.groupMembers.pageInfo);
                if (data.groupMembers.pageInfo.cursor && !cursorHistoryRef.current[currentPage + 1]) {
                    cursorHistoryRef.current[currentPage + 1] = data.groupMembers.pageInfo.cursor;
                }
            })
            .catch((err) => console.error("[useGroupMembers]", err))
            .finally(() => {
                inflightRef.current.delete(key);
                setIsLoading(false);
            });
    }, [groupId, currentPage, reloadKey, gql, initialLimit, filterKey]);

    const goToPage = useCallback((page: number) => setCurrentPage(page), []);

    const reload = useCallback(() => {
        cursorHistoryRef.current = [null];
        setCurrentPage(0);
        setReloadKey((k) => k + 1);
    }, []);

    return { users, pageInfo, currentPage, isLoading, goToPage, reload };
}

// ─── useGroupsUsersByBatch ────────────────────────────────────────────────────

const GROUPS_USERS_BY_BATCH_QUERY = `
  query GroupsUsersByBatch($userIds: [ID!]!, $limit: Int, $cursor: String) {
    groupsUsersByBatch(userIds: $userIds, limit: $limit, cursor: $cursor) {
      items {
        userId
        legalName { firstName lastName }
        emailId
        status
      }
      pageInfo {
        limit
        hasMore
        nextCursor
      }
    }
  }
`;

export function useGroupsUsersByBatch(userIds: string[], limit: number = 10) {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const accessToken = useAppSelector(selectAccessToken);
    void accessToken;

    const [users, setUsers] = useState<GroupBatchUser[]>([]);
    const [batchPageInfo, setBatchPageInfo] = useState<GroupBatchPageInfo | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const userIdsKey = userIds.join(",");
    const inflightRef = useRef<Set<string>>(new Set());

    const headers = useApiHeaders();
    const gql = useCallback(makeGql(dispatch, store, headers, GRAPHQL_ENDPOINT), [dispatch, store, headers]);

    useEffect(() => {
        if (!userIds.length) {
            setUsers([]);
            setBatchPageInfo(null);
            return;
        }

        const key = `batch-${userIdsKey}-${limit}`;
        if (inflightRef.current.has(key)) return;
        inflightRef.current.add(key);
        setIsLoading(true);
        setUsers([]);
        setBatchPageInfo(null);

        gql<{ groupsUsersByBatch: { items: GroupBatchUser[]; pageInfo: GroupBatchPageInfo } }>(GROUPS_USERS_BY_BATCH_QUERY, {
            userIds,
            limit,
        })
            .then((data) => {
                setUsers(data.groupsUsersByBatch.items);
                setBatchPageInfo(data.groupsUsersByBatch.pageInfo);
            })
            .catch((err) => console.error("[useGroupsUsersByBatch]", err))
            .finally(() => {
                inflightRef.current.delete(key);
                setIsLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userIdsKey, limit, gql]);

    const loadMore = useCallback(() => {
        if (!batchPageInfo?.hasMore || !batchPageInfo.nextCursor || isLoadingMore) return;
        const cursor = batchPageInfo.nextCursor;
        setIsLoadingMore(true);

        gql<{ groupsUsersByBatch: { items: GroupBatchUser[]; pageInfo: GroupBatchPageInfo } }>(GROUPS_USERS_BY_BATCH_QUERY, {
            userIds: userIds,
            limit,
            cursor,
        })
            .then((data) => {
                setUsers((prev) => [...prev, ...data.groupsUsersByBatch.items]);
                setBatchPageInfo(data.groupsUsersByBatch.pageInfo);
            })
            .catch((err) => console.error("[useGroupsUsersByBatch] loadMore", err))
            .finally(() => setIsLoadingMore(false));
    }, [batchPageInfo, isLoadingMore, userIds, limit, gql]);

    return {
        users,
        isLoading,
        isLoadingMore,
        hasMore: batchPageInfo?.hasMore ?? false,
        loadMore,
    };
}

// ─── useRemoveGroupMember ─────────────────────────────────────────────────────

const REMOVE_GROUP_MEMBER_MUTATION = `
  mutation RemoveGroupMember($groupId: ID!, $userId: ID!) {
    removeGroupMember(groupId: $groupId, userId: $userId)
  }
`;

export function useRemoveGroupMember() {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const accessToken = useAppSelector(selectAccessToken);
    void accessToken;

    const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const headers = useApiHeaders();
    const gql = useCallback(makeGql(dispatch, store, headers, GRAPHQL_ENDPOINT), [dispatch, store, headers]);

    const removeGroupMember = useCallback(
        async (groupId: string, userId: string): Promise<void> => {
            if (isMountedRef.current) setPendingIds((s) => new Set(s).add(userId));
            try {
                await gql<{ removeGroupMember: boolean }>(REMOVE_GROUP_MEMBER_MUTATION, { groupId, userId });
            } finally {
                if (isMountedRef.current) {
                    setPendingIds((s) => {
                        const n = new Set(s);
                        n.delete(userId);
                        return n;
                    });
                }
            }
        },
        [gql],
    );

    return { removeGroupMember, pendingIds };
}
